/**
 * We use Segment.com to aggregate the trackEvents and from there we
 * forward them to Pendo.io (destination) via api-to-api communication.
 * We don't use any Pendo.io snippet or library on the client, only the Segment snippet,
 * thus we can only send TRACK_EVENTS to Pendo through Segment. All other features provided
 * by Pendo cannot be used through Segment.
 */

export type Traits = Record<string, any> & {
  company: string;
  tenantType: string;
  email?: string;
  username?: string;
};

const eventNamePrefix = 'SIT_WEB';

// eslint-disable-next-line @typescript-eslint/ban-types
function isFunction(f: unknown): f is Function {
  return typeof f === 'function';
}

// eslint-disable-next-line @typescript-eslint/ban-types
const track = (event: string, payload?: Object | undefined): void => {
  if (isFunction(window.analytics?.track)) {
    window.analytics.track(`${eventNamePrefix}_${event}`, payload);
  }
};

/**
 * Method to identify the user with Segment => Pendo.
 *
 * @param id We should either use the `user_id` or `null` if the user is logged out.
 * Segment adds anonymousId automatically. [Read the docs](https://segment.com/docs/connections/spec/identify/#identities)
 * @param traits.company This is how Segment wants `company_id` to be passed.
 * @param traits.tenantType Used to filter out demo/developer companies.
 * @param options [Common Fields](https://segment.com/docs/connections/spec/common/)
 * @see https://segment.com/docs/connections/spec/identify/
 */
const identify = async (id: string | null, traits: Traits, options?: SegmentAnalytics.SegmentOpts | undefined): Promise<void> => {
  if (!window.analytics) return;

  const { identify: segmentIdentify, user } = window.analytics;

  if (isFunction(segmentIdentify)) {
    // user is not logged in
    if (!id) {
      segmentIdentify(traits, options);
      return;
    }
    if (isFunction(user) && isFunction(user()?.id) && user().id() === id) {
      // update the traits eitherway. The segment `user()` does not have a method to get the previous traits
      // and compare them with the new.
      user().traits(traits);
      return;
    }
    // otherwise
    segmentIdentify(id, traits, options);
  }
};

const analyticsClient = {
  track,
  identify,
};

export default analyticsClient;
